import { useFirestore, useFirestoreDocData, useUser } from "reactfire";
import { doc, runTransaction, setDoc } from "firebase/firestore";
import { FIRESTORE_CARTS_TABLE } from "../../../constants/FirebaseConstants";
import {
  addQuantityToCartLine,
  computeAddToCart,
  computeUpdatedCart,
} from "../../../helpers/cart";
import { db } from "../../../services/firebase";
import dayjs from "dayjs";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { sendExceptionToSlack } from "../../../services/api/exceptions";
import { trackCartAutoUpdated } from "../../../helpers/posthogAnalytics";

const EMPTY_DIFF = { removed: [], unavailable: [] };
const NOTHING_ADDED = { normalQty: 0, preOrderQty: 0 };

export function useCart() {
  const { t } = useTranslation();
  const firestore = useFirestore();
  const { data: user, status: userStatus } = useUser();
  const docRef = doc(
    firestore,
    FIRESTORE_CARTS_TABLE,
    userStatus === "loading" || !user ? "0" : user?.uid,
  );
  const { data, status } = useFirestoreDocData(docRef, { idField: "id" });
  const isLoading = status === "loading" || !user;

  /**
   * Every cart mutation goes through here. The cart is read *inside* the
   * transaction, so the mutation is always based on what is actually stored,
   * never on the copy this tab happens to hold in memory. Two tabs, a phone
   * that just woke up, a stale background tab: none of them can overwrite
   * the cart with an older version any more - Firestore retries the
   * transaction on contention and refuses it when offline.
   *
   * `mutate(storedProducts, storedDoc)` must be pure (it may run more than
   * once) and returns `{ products, extraFields, result }`; without `products`
   * nothing is written.
   */
  const runCartTransaction = async (mutate) => {
    if (!user?.uid) return null;
    const cartRef = doc(db, FIRESTORE_CARTS_TABLE, user.uid);
    return runTransaction(db, async (transaction) => {
      const snapshot = await transaction.get(cartRef);
      const storedDoc = snapshot.exists() ? snapshot.data() : {};
      const storedProducts = Array.isArray(storedDoc.products)
        ? storedDoc.products
        : [];
      const outcome = mutate(storedProducts, storedDoc);
      if (outcome && Array.isArray(outcome.products)) {
        transaction.set(
          cartRef,
          {
            products: outcome.products,
            updatedAt: dayjs().unix(),
            ...(outcome.extraFields || {}),
          },
          { merge: true },
        );
      }
      return outcome ? outcome.result : null;
    });
  };

  const addProductToCartLegacy = async ({
    product,
    quantity,
    notification,
  }) => {
    if (!user) return;
    try {
      await runCartTransaction((cart) => ({
        products: addQuantityToCartLine(cart, product, quantity || 1),
      }));
      if (notification) {
        message.success(t("added_to_cart"));
      }
    } catch (err) {
      console.log(err);
    }
  };

  // Centralized add with stock/pre-order split. Returns the actual split used.
  const addProductToCart = async ({ product, quantity, notification }) => {
    if (!user) return NOTHING_ADDED;
    try {
      const result = await runCartTransaction((cart) => {
        const { products, normalQty, preOrderQty } = computeAddToCart(cart, {
          product,
          quantity,
        });
        return { products, result: { normalQty, preOrderQty } };
      });
      if (notification) message.success(t("added_to_cart"));
      return result || NOTHING_ADDED;
    } catch (err) {
      console.log(err);
      return NOTHING_ADDED;
    }
  };

  const addProductSetToCart = async ({ products }) => {
    try {
      await runCartTransaction((cart) => ({
        products: products.reduce(
          (updatedCart, product) =>
            addQuantityToCartLine(
              updatedCart,
              product,
              product.quantity ? product.quantity : 1,
            ),
          cart,
        ),
      }));
    } catch (err) {
      console.log(err);
    }
  };

  const addMultipleProductsToCart = async ({ products }) => {
    try {
      await runCartTransaction((cart) => ({
        products: products.reduce(
          (updatedCart, product) =>
            addQuantityToCartLine(
              updatedCart,
              product,
              product.quantity ? product.quantity : 1,
            ),
          cart,
        ),
      }));
    } catch (err) {
      console.log(err);
    }
  };

  const decreaseProductQuantity = async ({ product }) => {
    const orderUnit = product.orderUnit || 1;
    try {
      await runCartTransaction((cart) => {
        const line = cart.find(
          (item) => item.cartItemId === product.cartItemId,
        );
        if (!line || !(line.quantity > orderUnit)) return null;
        return {
          products: cart.map((item) =>
            item.cartItemId === product.cartItemId
              ? { ...item, quantity: item.quantity - orderUnit }
              : item,
          ),
        };
      });
    } catch (err) {
      console.log(err);
    }
  };

  const deleteProductFromCart = async ({ product }) => {
    try {
      await runCartTransaction((cart) => {
        if (!cart.some((item) => item.cartItemId === product.cartItemId))
          return null;
        return {
          products: cart.filter(
            (cartItem) => cartItem.cartItemId !== product.cartItemId,
          ),
        };
      });
    } catch (err) {
      console.log(err);
    }
  };

  /**
   * Het hele mandje vervangen door deze producten (BMX-58: een offerte bewerken in de webshop).
   * Via de transactie, net als elke andere wijziging; de verdeling over voorraad en pre-order
   * volgt bij de eerstvolgende herberekening in ShopLayout.
   */
  const replaceCart = async ({ products }) =>
    runCartTransaction(() => ({
      products: products.reduce(
        (updatedCart, product) =>
          addQuantityToCartLine(
            updatedCart,
            product,
            product.quantity ? product.quantity : 1,
          ),
        [],
      ),
    }));

  const deleteAllProductsFromCart = async () => {
    try {
      if (user?.uid) {
        // Intentionally a plain overwrite: the order has been placed and the
        // cart, including any earlier auto-change notice, starts over
        await setDoc(doc(db, FIRESTORE_CARTS_TABLE, user?.uid), {
          products: [],
          updatedAt: dayjs().unix(),
        });
      }
    } catch (err) {
      console.log(err);
    }
  };

  /**
   * Bring the stored cart in line with the CMS. Runs from ShopLayout whenever
   * the product list changes. Returns `{ removed, unavailable }` for the tab
   * that ran it; the removal itself is also recorded on the cart document as
   * `lastAutoChange`, so every tab showing this cart can tell the customer.
   */
  const updateCart = async ({
    groupId,
    groupSettingsUserGroup,
    selectedSalesGroup,
    products,
  }) => {
    const localCart = data?.products;
    // A tab whose own copy of the cart is empty has nothing to recalculate;
    // if that copy is merely stale, another tab or the next run will do it
    if (
      !user?.uid ||
      !products ||
      products.length === 0 ||
      !localCart ||
      localCart.length === 0
    )
      return EMPTY_DIFF;

    // Side effects are collected here and fired after the transaction has
    // settled, because the transaction body may run more than once
    let written = null;
    let blocked = null;

    try {
      const diff = await runCartTransaction((storedCart) => {
        written = null;
        blocked = null;
        if (storedCart.length === 0) return { result: EMPTY_DIFF };

        const outcome = computeUpdatedCart(storedCart, {
          products,
          groupId,
          groupSettingsUserGroup,
          selectedSalesGroup,
        });
        const result = {
          removed: outcome.removed,
          unavailable: outcome.unavailable,
        };
        if (!outcome.changed) return { result };

        // Safety net: the only way a unit may leave the cart here is with a
        // product that vanished from the catalogue. Anything else is a bug,
        // and we would rather leave the cart alone than silently shrink it
        if (!outcome.quantityPreserved) {
          blocked = outcome;
          return { result: EMPTY_DIFF };
        }

        written = outcome;
        const extraFields =
          outcome.removed.length > 0
            ? {
                lastAutoChange: {
                  removed: outcome.removed,
                  at: dayjs().unix(),
                },
              }
            : {};
        return { products: outcome.products, extraFields, result };
      });

      if (blocked) {
        const summary =
          `Cart recalculation blocked for user ${user.uid}: total quantity ` +
          `${blocked.totalBefore} -> ${blocked.totalAfter} while only ` +
          `${blocked.removed.length} removed line(s) were reported`;
        // console.error is forwarded to Sentry by the CaptureConsole integration
        console.error(summary, blocked);
        sendExceptionToSlack(summary);
      }
      if (written || blocked) {
        trackCartAutoUpdated(data, written || blocked, { blocked: !!blocked });
      }

      return diff || EMPTY_DIFF;
    } catch (err) {
      // Offline, or contended beyond Firestore's retries: this tab must not
      // be the one that rewrites the cart, so nothing happened - by design
      console.log(err);
      return EMPTY_DIFF;
    }
  };

  return {
    data,
    isLoading,
    addProductToCartLegacy,
    addProductToCart,
    addProductSetToCart,
    addMultipleProductsToCart,
    replaceCart,
    decreaseProductQuantity,
    deleteProductFromCart,
    deleteAllProductsFromCart,
    updateCart,
  };
}
